import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { MeetingsListViewModel } from '../../../meetings-list.viewmodel';
import { SelectTimezones } from '../../../../../../global/select-timezone/select-timezones';
import { OERadioOptions } from '../../../../../../global/oe-radio-options/oe-radio-options';
import { DateFormats } from '../../../../../../../infrastructure/date-formatter';
import Text from '../../../../../../global/text/text';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { CSV_TEMPLATE_LINK } from '../../../../../../../constants/navigation.constants';
import UploadButton from '../../../../shared/upload-button';
import './upload-meeting-form.scss';
import { EnablementStatus, LinkTargetValue } from '../../../../../../../constants/common.constants';
import { MEETINGS_TEXT } from '../../../../../../../constants/common-labels.constants';
import { FormFieldInput } from '../../../../../../global/form/form-field-input/form-field-input';
import { V2_CONNECTIONS_MESSAGE_TEXT, CONNECTION_VERSION_TEXT } from './upload-meetings-form.texts';
import { hasPermission, isSuperUser } from '../../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../../api/roles.dao';

interface IProps {
    vm: MeetingsListViewModel;
    intl: IntlShape;
}

enum LocaleDateTypeFormat {
    us = 'US',
    eu = 'EU',
}

const connectionVersionInfoMessage = <FormattedMessage {...V2_CONNECTIONS_MESSAGE_TEXT} />;

const UploadMeetingsFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl } = props;
    const formData = props.vm.getMeetingFormDataVM();
    const shouldShowConnectionVersion = hasPermission(Permission.CREATE_MEETINGS_V2);

    return (
        <form className='oe-form upload-meetings-form'>
            <div className='oe-form_body'>
                {shouldShowConnectionVersion &&
                    <FormFieldInput.Toggle
                    isSelected={formData.isV2ConnectionVersion()}
                    toggle={formData.toggleConnectionVersion}
                    isDisabled={!isSuperUser()}
                    text={<Text
                        {...CONNECTION_VERSION_TEXT}
                        values={{
                            suffix: formData.isV2ConnectionVersion() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                        }}
                    />}
                    popoverText={connectionVersionInfoMessage}
                    className='upload-meetings-form__meeting-toggle'
                />}
                <FormField label={intl.formatMessage(MEETINGS_TEXT)}>
                    <UploadButton
                        fileName={props.vm.getCSVFileName()}
                        onChange={formData.setMeetingsField}
                        className='attendees-list-option attendees-list-option__in-modal'
                    />
                    {formData.showValidationErrors() &&
                        <span className='oe-form-field__error'>
                            {formData.getMeetingFieldError()}
                        </span>
                    }
                </FormField>
                <DefaultButton text={intl.formatMessage({
                        id: 'oe.csv.downloadExampleTemplate',
                        defaultMessage: 'Download example CSV template',
                        description: 'Label text'
                    })}
                               modifier='link'
                               onClick={() => window.open(CSV_TEMPLATE_LINK, LinkTargetValue.BLANK)}
                               className='upload-meetings-form_csv-example'

                />
                <FormField label={intl.formatMessage({
                        id: 'oe.meetings.selectDateFormat',
                        defaultMessage: 'Select date format',
                        description: 'Label text'
                    })}>
                    <Text
                        className='meeting-topic-hint'
                        id='oe.meetings.pleaseIndicateTheFormatOfTheMeetingDatesInThisCsvFile'
                        message='Please indicate the format of the Meeting Dates in this .csv file.'
                        description='Hint text'
                    />
                    <div className='oe-form-field__radio-group'>
                        <OERadioOptions
                            options={[
                                {
                                    value: LocaleDateTypeFormat.us,
                                    label: DateFormats.USDateFormat,
                                    id: 'oe.common.typeFormatUS'
                                },
                                {
                                    value: LocaleDateTypeFormat.eu,
                                    label: DateFormats.EUDateFormat,
                                    id: 'oe.common.typeFormatEU'
                                }
                            ]}
                            value={formData.dateFormatType}
                            onOptionToggle={mobx.action(value => formData.dateFormatType = value)}
                            disabled={false}
                        />
                    </div>
                    {formData.showValidationErrors() &&
                        <span className='oe-form-field__error'>
                            {formData.getDateFormatFieldError()}
                        </span>
                    }
                </FormField>
                <FormField label={intl.formatMessage({
                        id: 'oe.meetings.timeZone',
                        defaultMessage: 'Time Zone',
                        description: 'Label text'
                    })}>
                    <SelectTimezones
                        className='oe-form-field__control'
                        value={formData.selectedTimeZone}
                        onChange={mobx.action(value => formData.selectedTimeZone = value)}
                    />
                </FormField>
            </div>
        </form>
    );
}));

export { UploadMeetingsFormPresentation };
